import { defineStore } from "pinia";
import type {
  AllowListDomain,
  AllowListFile,
  AllowListResponse,
  AllowListUser,
} from "shared-core/types/allow-list";
import { AllowListType, ApprovalStatus } from "shared-core/types/allow-list";
import { DISALLOWED_DOMAINS } from "shared-core/utils/allow-list";
import { useApi } from "../../utils/api.ts";

export const CONSUMER_DOMAIN_ERROR = "CONSUMER_DOMAIN_NOT_ALLOWED";
export const DISALLOWED_DOMAIN_SAVE_ERROR = "DISALLOWED_DOMAIN_IN_SETTINGS";

export interface State {
  approved: {
    users: (Omit<AllowListUser, "createdAt"> | AllowListUser)[];
    domains: (Omit<AllowListDomain, "createdAt"> | AllowListDomain)[];
    files: (Omit<AllowListFile, "createdAt"> | AllowListFile)[];
  };
  pending: {
    users: AllowListUser[];
    domains: AllowListDomain[];
    files: AllowListFile[];
  };
  initialApproved: {
    users: AllowListUser[];
    domains: AllowListDomain[];
    files: AllowListFile[];
  };
}

interface AllowListStoreOptions {
  storeId: string;
  integrationPath: string;
}

function withoutPendingEntry(
  pending: State["pending"],
  type: AllowListType,
  value: string,
): State["pending"] {
  switch (type) {
    case AllowListType.USER:
      return {
        ...pending,
        users: pending.users.filter((user) => user.email !== value),
      };
    case AllowListType.DOMAIN:
      return {
        ...pending,
        domains: pending.domains.filter((domain) => domain.domain !== value),
      };
    case AllowListType.FILE:
      return {
        ...pending,
        files: pending.files.filter((file) => file.id !== value),
      };
  }
}

function approvePendingEntry(
  state: State,
  type: AllowListType,
  value: string,
): void {
  switch (type) {
    case AllowListType.USER: {
      const entry = state.pending.users.find((user) => user.email === value);
      if (!entry) return;
      state.pending.users = state.pending.users.filter(
        (user) => user !== entry,
      );
      state.approved.users.push(entry);
      state.initialApproved.users.push(entry);
      return;
    }
    case AllowListType.DOMAIN: {
      const entry = state.pending.domains.find(
        (domain) => domain.domain === value,
      );
      if (!entry) return;
      state.pending.domains = state.pending.domains.filter(
        (domain) => domain !== entry,
      );
      state.approved.domains.push(entry);
      state.initialApproved.domains.push(entry);
      return;
    }
    case AllowListType.FILE: {
      const entry = state.pending.files.find((file) => file.id === value);
      if (!entry) return;
      state.pending.files = state.pending.files.filter(
        (file) => file !== entry,
      );
      state.approved.files.push(entry);
      state.initialApproved.files.push(entry);
      return;
    }
  }
}

async function refreshQuietly(
  fetchData: () => Promise<void>,
): Promise<boolean> {
  try {
    await fetchData();
    return true;
  } catch (error) {
    console.error("Failed to refresh the allow list after a decision", error);
    return false;
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- returns Pinia's deeply-generic options-store StoreDefinition (non-literal string Id + full actions type); intractable to write by hand
export function createAllowListStore({
  storeId,
  integrationPath,
}: AllowListStoreOptions) {
  const basePath = `${integrationPath}/allow-list`;

  return defineStore(storeId, {
    state: (): State => ({
      approved: {
        users: [],
        domains: [],
        files: [],
      },
      pending: {
        users: [],
        domains: [],
        files: [],
      },
      initialApproved: {
        users: [],
        domains: [],
        files: [],
      },
    }),
    actions: {
      async fetchData() {
        const res = await useApi().get<AllowListResponse>(basePath);
        const data = await res.data;
        if (!data) return;

        this.approved = data.approved;
        this.pending = data.pending;
        this.initialApproved.users = structuredClone(data.approved.users);
        this.initialApproved.domains = structuredClone(data.approved.domains);
        this.initialApproved.files = structuredClone(data.approved.files);
      },

      addTrustedUser(email: string) {
        this.approved.users.push({ email });
      },

      removeTrustedUser(email: string) {
        this.approved.users = this.approved.users.filter(
          (user: AllowListUser | Omit<AllowListUser, "createdAt">) =>
            user.email !== email,
        );
      },

      addTrustedDomain(domain: string) {
        if (DISALLOWED_DOMAINS.includes(domain)) {
          throw new Error(CONSUMER_DOMAIN_ERROR);
        }
        this.approved.domains.push({ domain });
      },

      removeTrustedDomain(domain: string) {
        this.approved.domains = this.approved.domains.filter(
          (item: AllowListDomain | Omit<AllowListDomain, "createdAt">) =>
            item.domain !== domain,
        );
      },

      removeAllowedFile(id: string) {
        this.approved.files = this.approved.files.filter(
          (file: AllowListFile | Omit<AllowListFile, "createdAt">) =>
            file.id !== id,
        );
      },

      async saveSettings() {
        const usersToAdd = this.approved.users
          .filter(
            (user: AllowListUser | Omit<AllowListUser, "createdAt">) =>
              !this.initialApproved.users.some(
                (initial: AllowListUser) => initial.email === user.email,
              ),
          )
          .map((user: AllowListUser | Omit<AllowListUser, "createdAt">) => ({
            value: user.email,
            type: AllowListType.USER,
            status: ApprovalStatus.APPROVED,
          }));

        const usersToDelete = this.initialApproved.users
          .filter(
            (initialUser: AllowListUser) =>
              !this.approved.users.some(
                (
                  currentUser: AllowListUser | Omit<AllowListUser, "createdAt">,
                ) => currentUser.email === initialUser.email,
              ),
          )
          .map((user: AllowListUser) => ({
            value: user.email,
            type: AllowListType.USER,
          }));

        const hasDisallowedDomain = this.approved.domains.some(
          (domain: AllowListDomain | Omit<AllowListDomain, "createdAt">) =>
            DISALLOWED_DOMAINS.includes(domain.domain),
        );
        if (hasDisallowedDomain) {
          throw new Error(DISALLOWED_DOMAIN_SAVE_ERROR);
        }

        const domainsToAdd = this.approved.domains
          .filter(
            (domain: AllowListDomain | Omit<AllowListDomain, "createdAt">) =>
              !this.initialApproved.domains.some(
                (initialDomain: AllowListDomain) =>
                  initialDomain.domain === domain.domain,
              ),
          )
          .map(
            (domain: AllowListDomain | Omit<AllowListDomain, "createdAt">) => ({
              value: domain.domain,
              type: AllowListType.DOMAIN,
              status: ApprovalStatus.APPROVED,
            }),
          );

        const domainsToDelete = this.initialApproved.domains
          .filter(
            (initialDomain: AllowListDomain) =>
              !this.approved.domains.some(
                (
                  currentDomain:
                    AllowListDomain | Omit<AllowListDomain, "createdAt">,
                ) => currentDomain.domain === initialDomain.domain,
              ),
          )
          .map((domain: AllowListDomain) => ({
            value: domain.domain,
            type: AllowListType.DOMAIN,
          }));

        const filesToDelete = this.initialApproved.files
          .filter(
            (initialFile: AllowListFile) =>
              !this.approved.files.some(
                (file: AllowListFile | Omit<AllowListFile, "createdAt">) =>
                  file.id === initialFile.id,
              ),
          )
          .map((file: AllowListFile) => ({
            value: file.id,
            type: AllowListType.FILE,
          }));

        const toAdd = [...usersToAdd, ...domainsToAdd];
        const toDelete = [
          ...usersToDelete,
          ...domainsToDelete,
          ...filesToDelete,
        ];

        const requests: Promise<unknown>[] = [];

        if (toAdd.length > 0) {
          requests.push(
            useApi().post(basePath, {
              json: toAdd,
            }),
          );
        }

        if (toDelete.length > 0) {
          requests.push(
            useApi().delete(basePath, {
              json: toDelete,
            }),
          );
        }

        await Promise.all(requests);
        await this.fetchData();
      },

      async allow(type: AllowListType, value: string) {
        await useApi().post(basePath, {
          json: [{ type, value }],
        });
        await this.fetchData();
      },

      async requestAllow(type: AllowListType, value: string) {
        await useApi().post(`${integrationPath}/allow-list/request`, {
          json: { type, value },
        });
      },

      async approve(type: AllowListType, value: string) {
        await useApi().post(`${integrationPath}/allow-list/approve`, {
          json: { type, value },
        });
        approvePendingEntry(this, type, value);
        return refreshQuietly(() => this.fetchData());
      },

      async deny(type: AllowListType, value: string) {
        await useApi().post(`${integrationPath}/allow-list/deny`, {
          json: { type, value },
        });
        this.pending = withoutPendingEntry(this.pending, type, value);
        return refreshQuietly(() => this.fetchData());
      },
    },
  });
}
